import {
  Checkbox,
  Grid,
  Group,
  InputLabel,
  NumberInput,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { useMemo, useState } from "react";
import { useLanguage } from "../../../stores/LanguageStore";
import { useProfileStore } from "../../../stores/UserStore";
import { DatePickerInput, DateTimePicker, TimeInput } from "@mantine/dates";
import { defaultProgramData } from "../workflows/CreateCampaign/flow/flowSettings";
import { useFlowStore } from "../../../stores/WorkflowStore";
import { useModelsStore } from "../../../stores/ConfigStore";
import {
  IconCalendar,
  IconCalendarDue,
  IconCalendarTime,
  IconClock,
  IconHelp,
  IconRepeat,
} from "@tabler/icons-react";
import { useCampaignStore } from "../../../stores/CampaignStore";
import ProfileTimezone from "../../../components/View/ProfileTimezone";
import TimeIntervalSettings from "../workflows/CreateCampaign/campaignSettings/TimeIntervalSettings";
import { getPermission } from "../../../functions/utilFunctions";
import NewsletterTimeIntervalSettings from "./NewsletterTimeIntervalSettings";
import ProgramCard from "./ProgramCard";
import dayjs from "dayjs";

const NewsletterAdvancedSettings = () => {
  const programData = useCampaignStore((s) => s.programData);
  const setProgramData = useCampaignStore((s) => s.setProgramData);

  const timeStart = useCampaignStore((s) => s.timeStart);
  const setTimeStart = useCampaignStore((s) => s.setTimeStart);
  const [editTime, setEditTime] = useState(false);

  const [editDate, setEditDate] = useState(false);

  const lang = useLanguage((s) => s.language);

  const [useAB, setUseAB] = useState(!!programData?.ab_subject_2);

  const nodes = useFlowStore((s) => s.nodes);

  const subject1 = useMemo(() => {
    let first_node = nodes.find(
      (x) => x.type === "action" && (x?.data?.is_initial || x?.data?.initial),
    );
    if (
      first_node?.data?.actionType === "email" &&
      first_node?.data?.model_id
    ) {
      let model = useModelsStore
        .getState()
        ?.model_emails?.find((x) => x.id === first_node?.data?.model_id);

      return model?.subject || " - ";
    }
    return null;
  }, [nodes]);

  const days = useMemo(
    () => [
      {
        value: "allowed_monday",
        label: lang.workflows.program.step_config.more.days.monday,
      },
      {
        value: "allowed_tuesday",
        label: lang.workflows.program.step_config.more.days.tuesday,
      },
      {
        value: "allowed_wednesday",
        label: lang.workflows.program.step_config.more.days.wednesday,
      },
      {
        value: "allowed_thursday",
        label: lang.workflows.program.step_config.more.days.thursday,
      },
      {
        value: "allowed_friday",
        label: lang.workflows.program.step_config.more.days.friday,
      },
      {
        value: "allowed_saturday",
        label: lang.workflows.program.step_config.more.days.saturday,
      },
      {
        value: "allowed_sunday",
        label: lang.workflows.program.step_config.more.days.sunday,
      },
    ],
    [lang],
  );

  const updateData = (field, value) => {
    setProgramData({
      ...programData,
      [field]: value,
    });
  };

  return (
    <>
      <Grid gutter="md" mb="md">
        {/* 2. HEURE DE DÉBUT SCÉNARIO */}
        <Grid.Col span={{ base: 12, md: 6 }}>
          <ProgramCard
            icon={<IconClock size={24} />}
            label={lang.newsletter.start_time_label}
            valueDisplay={timeStart}
            helpText={
              lang.workflows.program.step_config.time_start_sending_help
            }
            rightBottom={
              programData?.time_sending_timezone ||
              Intl.DateTimeFormat().resolvedOptions().timeZone
            }
            isEditing={editTime}
            setIsEditing={setEditTime}
            editorComponent={
              <TimeInput
                value={timeStart}
                onChange={(e) => {
                  setTimeStart(e.target.value);
                  updateData("time_start_sending", e.target.value);
                }}
                leftSection={<IconClock size={16} />}
                withSeconds
                size="sm"
                radius="md"
                w="100%"
                autoFocus
              />
            }
          />
        </Grid.Col>

        {/* 4. ENVOI AUTOMATIQUE */}
        <Grid.Col span={{ base: 12, md: 6 }}>
          <ProgramCard
            icon={<IconRepeat size={24} />}
            label={lang.newsletter.limit_date_label}
            valueDisplay={dayjs(
              programData?.limit_date
                ? new Date(programData.limit_date)
                : new Date(),
            ).format("DD MMM YYYY à HH:mm A")}
            isEditing={editDate}
            setIsEditing={setEditDate}
            editorComponent={
              <DateTimePicker
                onChange={(val) => {
                  updateData("limit_date", val?.toISOString());
                }}
                value={
                  programData?.limit_date
                    ? new Date(programData.limit_date)
                    : new Date()
                }
                leftSection={<IconCalendarTime size={16} />}
                valueFormat="DD MMM YYYY à HH:mm A"
                size="sm"
                radius="md"
                w="100%"
                autoFocus
              />
            }
          />
        </Grid.Col>
      </Grid>

      {getPermission("resendToBounce") && (
        <Checkbox
          mb={"sm"}
          label={lang.workflows.program.step_config.more.resend_to_bounce}
          name="resend_to_bounce"
          size="md"
          mt={4}
          defaultChecked={
            programData?.resend_to_bounce
              ? programData.resend_to_bounce
              : defaultProgramData.resend_to_bounce
          }
        />
      )}

      {getPermission("priorityCampaign") && (
        <Checkbox
          mb={"xs"}
          label={lang.workflows.program.step_config.more.is_prior}
          name="priority_email"
          size="md"
          mt={8}
          defaultChecked={
            programData?.priority_email
              ? programData.priority_email
              : defaultProgramData.priority_email
          }
        />
      )}

      <Group wrap="nowrap">
        <InputLabel size="md" w={180}>
          {lang.workflows.program.step_config.time_stop_sending}
        </InputLabel>
        <TimeInput
          name="time_stop_sending"
          required
          defaultValue={
            programData?.time_stop_sending ||
            defaultProgramData.time_stop_sending
          }
          onChange={(e) =>
            useCampaignStore.getState().setTimeStop(e.target.value)
          }
          leftSection={<IconClock />}
          rightSection={
            <ProfileTimezone
              timezone={
                programData?.time_sending_timezone ||
                Intl.DateTimeFormat().resolvedOptions().timeZone
              }
            />
          }
          rightSectionWidth={"auto"}
          w={300}
          size="md"
          withSeconds
        />
      </Group>

      <Text size="md" mt={8}>
        {lang.workflows.program.step_config.more.allowed_days}
      </Text>

      <Text mb={"sm"} size="sm" fw={600}>
        {lang.workflows.program.step_config.more.allowed_days_help}
      </Text>
      <Stack w={"100%"}>
        {days.map((item) => (
          <NewsletterTimeIntervalSettings
            key={item.value}
            item={item}
            programData={programData}
            setProgramData={setProgramData}
          />
        ))}
      </Stack>

      <Group wrap="nowrap" mt={"xl"}>
        <InputLabel size="md" w={180}>
          {lang.workflows.program.step_config.more.date_stop}
        </InputLabel>
        <DateTimePicker
          dropdownType="modal"
          placeholder={lang.workflows.program.step_config.more.date_stop}
          name="date_stop"
          radius={"md"}
          size="md"
          valueFormat="DD MMM YYYY à HH:mm A"
          leftSection={<IconCalendarDue />}
          rightSectionWidth={"auto"}
          w={330}
          rightSection={<ProfileTimezone />}
          defaultValue={
            programData?.date_stop
              ? new Date(programData?.date_stop || null)
              : undefined
          }
        />
      </Group>

      <Checkbox
        label={lang.workflows.program.step_config.more.stop_steps_at_end_date}
        name="stop_steps_at_end_date"
        mt={"xs"}
        size="md"
        defaultChecked={
          programData?.stop_steps_at_end_date
            ? programData.stop_steps_at_end_date
            : defaultProgramData.stop_steps_at_end_date
        }
        //   disabled={isStopDisabled}
      />

      <NumberInput
        label={lang.workflows.program.step_config.more.limit_total_sending}
        // description={
        //   lang.workflows.program.step_config.more.limit_total_sending_0
        // }
        mt={"xl"}
        styles={{
          description: {
            fontSize: 12,
          },
        }}
        size="md"
        min={0}
        name="limit_total_sending"
        defaultValue={
          programData?.limit_total_sending ||
          defaultProgramData.limit_total_sending
        }
      />

      {/* Disable if we find an IA model */}
      {subject1 && !location.href.includes("quick") && (
        <>
          <Group justify="space-between" mt={"xl"}>
            <Tooltip label={lang.campaign.already_programmed} refProp="rootRef">
              <Checkbox
                label={lang.workflows.program.step_config.more.use_ab}
                checked={useAB}
                onChange={(e) => setUseAB(e.target.checked)}
                styles={{
                  label: {
                    fontWeight: 500,
                  },
                }}
                size="md"
                mb={8}
              />
            </Tooltip>

            <Tooltip
              label={lang.workflows.program.step_config.more.ab_desc}
              withArrow
            >
              <IconHelp size={21} opacity={0.75} stroke={1} />
            </Tooltip>
          </Group>
          <NumberInput
            label={lang.workflows.program.step_config.more.proportion}
            min={1}
            max={99}
            name="ab_proportion"
            disabled={!useAB}
            defaultValue={programData?.ab_proportion || 10}
            required
            size="md"
          />
          <TextInput
            label={lang.workflows.program.step_config.more.subject1}
            placeholder={lang.workflows.program.step_config.more.subject1}
            value={subject1}
            disabled={!useAB}
            readOnly
            size="md"
            mt={"sm"}
          />
          <TextInput
            label={lang.workflows.program.step_config.more.subject2}
            placeholder={lang.workflows.program.step_config.more.subject2}
            name="ab_subject_2"
            disabled={!useAB}
            defaultValue={programData?.ab_subject_2}
            size="md"
            mt={"sm"}
          />
          <TextInput
            label={lang.workflows.program.step_config.more.subject3}
            placeholder={lang.workflows.program.step_config.more.subject3}
            name="ab_subject_3"
            disabled={!useAB}
            defaultValue={programData?.ab_subject_3}
            size="md"
            mt={"sm"}
          />
        </>
      )}
    </>
  );
};

export default NewsletterAdvancedSettings;
